console.log("🔥 FILE LOADED FROM:", import.meta.url);

import { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Button,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import useGeoJson from "../hooks/useGeoJson";
import useCampaignIntel from "../hooks/useCampaignIntel";
import SectorMapView from "./SectorMapView";
import SectorSearchBar from "./SectorSearchBar";
import FlyerLayoutModal from "./flyer/FlyerLayoutModal";
import MapGpsButton from "./MapGpsButton";
import MapLegend from "./MapLegend";
import MapHoverCard from "./MapHoverCard";
import MobileBottomSheet from "./MobileBottomSheet";

import { normalizeSectorName } from "../utils/normalizeSectorName";
import { resolveRegionCode } from "../utils/resolveRegionCode";
import { normalizeIntelKeys } from "../utils/IntelNormalizer";
import { loadSectorSlots } from "../hooks/useSectorSlotLoader";
import { supabase } from "../utils/supabaseClient";

const MotionBox = motion(Box);

// PR fallback center
const defaultCenter = [53.7632, -2.7044];

// MultiPolygon‑safe centroid
function getFeatureCenter(feature) {
  try {
    const geom = feature.geometry;

    if (geom.type === "Polygon") {
      const coords = geom.coordinates[0];
      const lats = coords.map(c => c[1]);
      const lngs = coords.map(c => c[0]);
      return [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lngs) + Math.max(...lngs)) / 2,
      ];
    }

    if (geom.type === "MultiPolygon") {
      const flat = geom.coordinates.flat(2);
      const lats = flat.map(c => c[1]);
      const lngs = flat.map(c => c[0]);
      return [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lngs) + Math.max(...lngs)) / 2,
      ];
    }

    return null;
  } catch {
    return null;
  }
}

export default function PostcodeCouponsMap({
  authId,
  flyerId,
  region,
  setRegionOverride,
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const isPublic = !authId;

  const normalizedRegion = (region || "PR").toUpperCase();

  const isMobile =
    typeof window !== "undefined" &&
    (window.innerWidth < 768 ||
      window.matchMedia("(orientation: portrait)").matches);

  const [publicCache, setPublicCache] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [pendingSearchSector, setPendingSearchSector] = useState(null);
  const [activeFlyerId, setActiveFlyerId] = useState(flyerId ?? null);
  const [searchHighlight, setSearchHighlight] = useState(null);

  const [recentSearches, setRecentSearches] = useState([]);
  const mapRef = useRef(null);

  const modal = useDisclosure();

  // PUBLIC CACHE
  useEffect(() => {
    if (!isPublic) return;

    const loadPublicCache = async () => {
      console.log("[PCM] 🌍 Loading public_map_cache via RPC…");
      const { data, error } = await supabase.rpc("get_public_map_cache");
      if (error) {
        console.error("[PCM] ❌ public_map_cache error:", error);
      }
      setPublicCache(data ?? []);
      console.log("[PCM] 📦 public_map_cache rows:", data?.length);
    };

    loadPublicCache();
  }, [isPublic]);

  // INTEL (cockpit)
  const { intel } = useCampaignIntel(authId, toast);
  const normalizedIntel = useMemo(() => normalizeIntelKeys(intel), [intel]);

  // GEOJSON
  const { geoData, loadingGeo } = useGeoJson(normalizedRegion, toast);

  const extractSectorName = (props) =>
    props?.name ||
    props?.sector ||
    props?.postcode ||
    props?.label ||
    props?.pcd_sector ||
    props?.pcd ||
    props?.pcd2 ||
    props?.pcd_sect ||
    props?.pcd_sector_code ||
    "";

  // MERGE
  const mergedGeoData = useMemo(() => {
    if (!geoData) return geoData;

    if (isPublic) {
      const cacheMap = Object.fromEntries(
        (publicCache || []).map((row) => [
          normalizeSectorName(row.postcode_sector),
          row,
        ])
      );

      const features = geoData.features.map((f) => {
        const rawName = extractSectorName(f.properties);
        const sector = normalizeSectorName(rawName);
        const cached = cacheMap[sector] || {};

        return {
          ...f,
          properties: {
            ...f.properties,
            sector,
            households: cached.households ?? null,
            bookedSlots: cached.booked_slots ?? null,
            slots_available: cached.booked_slots ?? null,
          },
        };
      });

      return { ...geoData, features };
    }

    const features = geoData.features.map((f) => {
      const rawName = extractSectorName(f.properties);
      const sector = normalizeSectorName(rawName);
      const intelForSector = normalizedIntel[sector] || {};

      return {
        ...f,
        properties: {
          ...f.properties,
          sector,
          households: intelForSector.households ?? null,
          roi: intelForSector.roi_estimate ?? intelForSector.roi ?? null,
          slots_available: intelForSector.slots_available ?? null,
          flyer_id: intelForSector.flyer_id ?? null,
        },
      };
    });

    return { ...geoData, features };
  }, [geoData, publicCache, normalizedIntel, isPublic]);

  // AUTO-CENTER REGION
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  useEffect(() => {
    if (!mergedGeoData?.features?.length) return;
    if (pendingSearchSector) return;
    if (selectedSector) return;

    const first = mergedGeoData.features[0];
    const center = getFeatureCenter(first) || defaultCenter;

    console.log("[PCM] 🧭 auto-center region:", center);
    setMapCenter(center);
  }, [normalizedRegion, mergedGeoData, pendingSearchSector, selectedSector]);

  // CLICK HANDLER
  const handleSectorClick = async (sectorCodeRaw) => {
    const sectorCode = normalizeSectorName(sectorCodeRaw);

    let match = null;
    let center = null;

    if (mergedGeoData?.features?.length) {
      match = mergedGeoData.features.find((f) => {
        const rawName = extractSectorName(f.properties);
        return normalizeSectorName(rawName) === sectorCode;
      });

      if (match) center = getFeatureCenter(match);
    }

    setSelectedSector(match?.properties || { sector: sectorCode });
    setSearchHighlight(null);

    if (center) {
      console.log("[PCM] 🧭 pan on click:", center);
      setMapCenter(center);
    }

    if (!isPublic) {
      const { flyerId: flyerIdForSector, slots } = await loadSectorSlots(
        sectorCode,
        normalizedRegion,
        toast
      );

      setActiveFlyerId(flyerIdForSector || null);
      setSelectedSlots(slots || []);
      if (flyerIdForSector) modal.onOpen();
    }
  };

  // SEARCH HANDLER
  const handleSearchRaw = (rawInput) => {
    console.log("--------------------------------------------------");
    console.log("[PCM] 🔍 handleSearchRaw:", rawInput);

    const sectorCode = normalizeSectorName(rawInput);
    if (!sectorCode) return;

    const targetRegion = resolveRegionCode(sectorCode);

    setSearchHighlight(sectorCode);
    setSelectedSector(null);

    if (targetRegion && targetRegion !== normalizedRegion) {
      setRegionOverride?.(targetRegion);
      setPendingSearchSector(sectorCode);
      return;
    }

    const match = mergedGeoData?.features?.find((f) => {
      const rawName = extractSectorName(f.properties);
      return normalizeSectorName(rawName) === sectorCode;
    });

    if (!match) return;

    const center = getFeatureCenter(match);

    if (center) {
      console.log("[PCM] 🧭 pan on search:", center);
      setMapCenter(center);
    }
  };

  // NEW: handler used by overlay (search-first UX)
  const handleMapSearch = async (rawQuery) => {
    const sector = normalizeSectorName(rawQuery);
    if (!sector) {
      toast({
        title: "Invalid postcode",
        description: "Please enter a valid postcode sector (e.g., FY4).",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Save recent
    setRecentSearches((s) => {
      const next = [sector, ...s.filter(x => x !== sector)].slice(0, 10);
      return next;
    });

    // If region differs, trigger region override flow
    const targetRegion = resolveRegionCode(sector);
    if (targetRegion && targetRegion !== normalizedRegion) {
      setRegionOverride?.(targetRegion);
      setPendingSearchSector(sector);
      return;
    }

    // Find feature and center
    const match = mergedGeoData?.features?.find((f) => {
      const rawName = extractSectorName(f.properties);
      return normalizeSectorName(rawName) === sector;
    });

    setPendingSearchSector(sector);
    setSelectedSector(null);
    setSearchHighlight(sector);

    if (!match) {
      toast({
        title: "Sector not found",
        description: `We couldn't find ${sector} in this region.`,
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const center = getFeatureCenter(match);
    if (center) {
      setMapCenter(center);
    }

    setSelectedSector(match.properties || { sector });
    setPendingSearchSector(null);
  };

  // Geolocate handler for overlay
  const handleGeolocate = () => {
    if (!navigator.geolocation) {
      toast({ title: "Geolocation not available", status: "info", duration: 3000, isClosable: true });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setMapCenter([lat, lng]);
      },
      () => {
        toast({ title: "Unable to get location", status: "error", duration: 3000, isClosable: true });
      }
    );
  };

  // PENDING SEARCH (after region switch)
  useEffect(() => {
    if (!pendingSearchSector) return;
    if (!mergedGeoData?.features?.length) return;

    const sectorCode = normalizeSectorName(pendingSearchSector);

    const match = mergedGeoData.features.find((f) => {
      const rawName = extractSectorName(f.properties);
      return normalizeSectorName(rawName) === sectorCode;
    });

    if (!match) {
      setPendingSearchSector(null);
      return;
    }

    const center = getFeatureCenter(match);

    if (center) {
      console.log("[PCM] 🧭 pan after region switch:", center);
      setMapCenter(center);
    }

    setPendingSearchSector(null);
  }, [pendingSearchSector, mergedGeoData]);

  // Clear pendingSearchSector after a short delay if set by overlay
  useEffect(() => {
    if (!pendingSearchSector) return;
    const t = setTimeout(() => setPendingSearchSector(null), 3000);
    return () => clearTimeout(t);
  }, [pendingSearchSector]);

  const selectedSectorCode =
    selectedSector?.sector || selectedSector?.name || null;

  if (loadingGeo || !mergedGeoData) {
    return (
      <Flex align="center" justify="center" p={6} direction="column">
        <Spinner size="xl" color="red.500" />
        <Heading size="sm" mt={4} color="red.600">
          Loading map…
        </Heading>
      </Flex>
    );
  }

  return (
    <Box w="100%" h="80vh" position="relative">
      <Flex
        justify="space-between"
        align="center"
        mb={3}
        px={2}
        flexWrap="wrap"
        gap={2}
      >
        <Heading size="md" color="red.700">
          Postcode Sector Map
        </Heading>

        {/* Make the search the focal point: promote SectorSearchBar to a prominent, full-width control */}
        <Box w="100%" maxW="920px" mx="auto">
          <SectorSearchBar onSearch={handleSearchRaw} colorScheme="red" size="lg" />
        </Box>

        {/* RESET BUTTON REMOVED */}
      </Flex>

      <MotionBox
        borderRadius="md"
        overflow="visible"
        shadow="md"
        h="100%"
        bg="white"
        position="relative"
      >
        <SectorMapView
          ref={mapRef}
          geoData={mergedGeoData}
          mapCenter={mapCenter}
          usingUkFallback={false}
          onSectorClick={handleSectorClick}
          highlightSector={selectedSectorCode}
          searchHighlight={searchHighlight}
          outlineColor="red"
          activeColor="rgba(255,0,0,0.55)"
          authId={authId}
          setRegionOverride={setRegionOverride}
          normalizedRegion={normalizedRegion}
          isMobile={isMobile}
        />

        <MapLegend />

        <MapGpsButton
          onLocate={() => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const { latitude, longitude } = pos.coords;
                setMapCenter([latitude, longitude]);
              },
              () => toast({ title: "Location unavailable", status: "warning" })
            );
          }}
        />

        {!isMobile && selectedSector && (
          <MapHoverCard
            sector={selectedSector}
            isMobile={false}
            isBusiness={!isPublic}
            onViewFlyer={() => navigate(`/flyer/${selectedSectorCode}`)}
            onClose={() => setSelectedSector(null)}
          />
        )}

        {isMobile && selectedSector && (
          <MobileBottomSheet
            sector={selectedSector}
            isBusiness={!isPublic}
            onViewFlyer={() => navigate(`/flyer/${selectedSectorCode}`)}
            onClose={() => setSelectedSector(null)}
          />
        )}
      </MotionBox>

      {!isPublic && (
        <FlyerLayoutModal
          isOpen={modal.isOpen}
          onClose={modal.onClose}
          sector={selectedSectorCode}
          slots={selectedSlots}
        />
      )}

      {isPublic && selectedSectorCode && (
        <Flex justify="center" mt={4}>
          <Button
            colorScheme="red"
            size="lg"
            onClick={() => navigate(`/flyer/${selectedSectorCode}`)}
          >
            View Coupons →
          </Button>
        </Flex>
      )}
    </Box>
  );
}
